module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    attrs: {
      "epub:type": "bodymatter",
      "title": "Part 1. Positioning Speech: Scholem’s Political Education"
    }
  }, [_c('p', {
    staticClass: "pt_num",
    attrs: {
      "id": "sec4"
    }
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_11",
      "title": "11"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/9780253062093_Contents#rsec4"
    }
  }, [_c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("PART 1")])])])]), _vm._v(" "), _c('p', {
    staticClass: "pt_title"
  }, [_c('a', {
    attrs: {
      "href": "#/text/9780253062093_Contents#rsec4"
    }
  }, [_vm._v("POSITIONING SPEECH: SCHOLEM’S POLITICAL EDUCATION")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent_para"
  }, [_vm._v("The title of Scholem’s memoir "), _c('i', [_vm._v("From Berlin to Jerusalem")]), _vm._v(" places emphasis on the most distinctive trajectory of his biography. Turning away from a fully assimilated parental home and toward Judaism, he went on to become one of Judaism’s most significant interpreters—or even its embodiment, for many. He subjected German Judaism to an extremely sharp critique, distancing himself from his origin in a manner that intensifies the tension inherent in any biography, between the story of a life as it is lived and as it is narrated in retrospect. Yet this tension hardly surfaces in Scholem’s text. With the calm hand of a memoirist, he details the experiences and encounters along the path laid out by his title: “This path appeared to me to be singularly direct and illuminated by clear signposts” ("), _c('i', [_vm._v("FBJ")]), _vm._v(", 1). The text foregrounds clarity, set up in contrast to the milieu of Scholem’s origin. He casts assimilated Judaism as “self-deception,” noting that “its discovery was one of the most decisive experiences of my youth” ("), _c('i', [_vm._v("FBJ")]), _vm._v(", 26). Thus, it was “no miracle” that he became a Zionist ("), _c('i', [_vm._v("FBJ")]), _vm._v(", 40). Drama, crisis, "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_12",
      "title": "12"
    }
  }, [_vm._v("and the problem of memory, typical themes for the genre of the autobiography, play only a secondary role in the text."), _c('sup', [_c('a', {
    attrs: {
      "id": "fnpt1-1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#rfnpt1-1"
    }
  }, [_vm._v("1")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent_para"
  }, [_vm._v("Published much later than his memoir, Scholem’s early letters, diaries, and essays present a very different picture of his development. These texts demonstrate that his path was not smooth and direct but marked by intense crises and conflicts, and that these crises and conflicts remained far less resolved than the memoir suggests. Scholem began his diaries in 1913, at the age of fifteen, and he wrote with relative consistency until the early 1920s. In these diaries, reflections generally outweigh mere observation. They are full of judgments, indictments, and justifications aimed at himself and others as well as attempts to say something significant regarding Judaism and Zionism. Elements that come to light only in the margins of the memoir dominate pages of the diaries.")]), _vm._v(" "), _c('p', {
    staticClass: "indent_para"
  }, [_vm._v("Schematically, we can divide the diaries into three phases. In the first period, from 1913 to around the summer of 1916, entries are primarily composed of short notes on everyday experiences and thoughts. The bulk of diary entries date from a second phase, between the summer of 1916 and the summer of 1919. Alongside daily reports and more and more self-reflection, there are stand-alone texts and collections of fragments in which Scholem reflects on Judaism, Zionism, and related topics, striving to sketch something like a theory of Judaism. I will explore this theory at length in "), _c('span', {
    attrs: {
      "href": "#/text/9780253062093_Part02",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("part 2")]), _vm._v(". In the third phase, from 1919 to 1923, there is a sharp drop-off in regular notes and an increase in stylized compositions. Independent texts, often extremely esoteric in nature, supersede the openness typical of the diary genre. The still-unpublished diaries from after 1923 also resemble those from this third phase: with some exceptions, such as the account of Scholem’s trip to Europe in 1946, there are hardly any continuous passages chronicling events."), _c('sup', [_c('a', {
    attrs: {
      "id": "fnpt1-2"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#rfnpt1-2"
    }
  }, [_vm._v("2")])]), _vm._v(" Scattered throughout, there are also private texts and poems written with an eye to self-understanding, to which we will return below. Scholem’s letters reveal a similar development toward increased "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_13",
      "title": "13"
    }
  }, [_vm._v("stylization, becoming more and more erratic and often difficult to understand, even for their original recipients."), _c('sup', [_c('a', {
    attrs: {
      "id": "fnpt1-3"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#rfnpt1-3"
    }
  }, [_vm._v("3")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent_para"
  }, [_vm._v("While Scholem’s letters and diaries were not written or intended for publication, he carefully saved them and drew on them extensively, not only as material for his autobiographical texts but also as a source of formulations to be further developed in his later writing. This double function informs my reading here, allowing me to trace the young Scholem’s development within a network of relationships, readings, and ideas. These are also Scholem’s first attempts at writing, in which he experiments with different formulations, styles, genres, and figures of thought in order to find a way to express himself. The development of his intellectual position and the acquisition of a language of his own—the formation of lifestyle and style of utterance—thus go hand in hand.")]), _vm._v(" "), _c('p', {
    staticClass: "indent_para"
  }, [_vm._v("Scholem’s memoir and his diaries are not simply private texts. He wrote as a Jew, and his texts appeared in the context of the “Jewish question” and the “cultural revolution” of Judaism in the early twentieth century, which led to a proliferation of discourses on Jewish belonging that still hold fascination for us today. For lack of political institutions, the “Jewish question” was negotiated in Jewish literature, in the broadest sense. Conversely, this means that such literature has a particular relation to the question of belonging; Jewish discourse of the period is metapolitical, as it envisions a community that does not yet exist. This is especially clear with respect to cultural Zionism, serving the specifically German semantics of "), _c('i', [_vm._v("Kultur")]), _vm._v(", a culture that helps to simultaneously articulate national particularity outward and a societal claim to leadership inward."), _c('sup', [_c('a', {
    attrs: {
      "id": "fnpt1-4"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#rfnpt1-4"
    }
  }, [_vm._v("4")])]), _vm._v(" Cultural politics as well as metapolitics are the domain of intellectuals, those who occupy neither a fixed position in society’s institutions nor a stand-alone position of unquestioned “artistic” subjectivity."), _c('sup', [_c('a', {
    attrs: {
      "id": "fnpt1-5"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#rfnpt1-5"
    }
  }, [_vm._v("5")])]), _vm._v(" Such intellectuals do not avail themselves of special scholarly or aesthetic discourses, instead employing continually contested everyday language. Claiming to be the legitimate speaker of a group, the intellectual actually generates this group through an “alchemy of representation”: “Group made man, he personifies a fictitious person, which he lifts out of the state of a simple aggregate of separate individuals, enabling them to act and speak, through him, ‘like a single person’. Conversely, he receives "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_14",
      "title": "14"
    }
  }, [_vm._v("the right to speak and act in the name of the group, to ‘take himself for’ the group he incarnates.”"), _c('sup', [_c('a', {
    attrs: {
      "id": "fnpt1-6"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#rfnpt1-6"
    }
  }, [_vm._v("6")])]), _vm._v(" With respect to Judaism in the early twentieth century, this alchemy took place in a particular field of power: Jewish discourse was not only the discourse of a minority but also part of a complex mass movement—namely, Zionism—and thus its politics of representation differed from the lonely incantations of invisible communities that some German intellectuals developed during this period.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent_para"
  }, [_vm._v("Within this framework, the political dimension of Scholem’s writing does not consist primarily in his opinions and judgments, which—unsurprisingly for a young Jew of the day—are numerous, radical, and idiosyncratic. Instead, it is the relationship of his discourse to the community that he represents that is political. Seeking a place for himself, he struggles for legitimacy as a Jew and as an intellectual, constantly distinguishing between “true” and “false” representatives of Judaism and developing an ever more complex account of his own position.")]), _vm._v(" "), _c('p', {
    staticClass: "indent_para"
  }, [_vm._v("Inquiring into the political dimension of Scholem’s writing allows for a meaningful analysis of his use of language, from tentative borrowings from other languages, to rhetorical mechanisms of self-legitimation, polemics, and explicit reflection, to various forms of indirect communication, as well as reflection on political linguistic phenomena. Rather than being a simple precondition that “naturally” sets the course of his life, Scholem’s Jewishness becomes recognizable in writing aimed at the acquisition of a language of his own. Through a complex process, the direct, appellative gesture of his earliest writings disappears as his texts become more and more “objective” and his judgments more stable. Invoking silence again and again, his formulations become deliberately opaque, and his language becomes highly evocative, suggesting something while saying nothing fixed. Over time, this language spreads across different stages and genres, with Scholem writing as a historian, as a political essayist, as a contemporary, and as an autobiographer, gaining the pronounced authority with which he ultimately interprets the story of his own life.")]), _vm._v(" "), _c('p', {
    staticClass: "indent_para"
  }, [_vm._v("The chapters of "), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01"
    }
  }, [_vm._v("part 1")]), _vm._v(" will trace the different phases of Scholem’s shifting political ethos, analyzing the correlation between his imagined position and "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_15",
      "title": "15"
    }
  }, [_vm._v("his mode of writing. His philosophical reflections and central relationship to Jewish tradition will be addressed in "), _c('span', {
    attrs: {
      "href": "#/text/9780253062093_Part02",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("part 2")]), _vm._v(". We will first consider how Scholem adopted language from the youth movement while at the same time striving to find his own radical position and language. This attempt, described here in terms of a revolutionary and Romantic ethos, reached a crisis with Scholem’s break from the youth movement and with Buber; he subsequently developed a new ascetic ethos, essentially consisting in the withdrawal of direct expression. This position also allowed Scholem to process his disappointment in light of the actual Zionism that he experienced upon immigration to Palestine in 1923. After World War II, he was circumspect in speaking out on these questions, while he found a form that allowed him to interpret his own origin in his essays on German Jewry and contemporaries.")])]), _vm._v(" "), _c('p', {
    staticClass: "line"
  }), _vm._v(" "), _c('p', {
    staticClass: "note_para"
  }, [_c('a', {
    attrs: {
      "id": "rfnpt1-1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#fnpt1-1"
    }
  }, [_vm._v("1")]), _vm._v(". Scholem seldom explicitly distinguishes between the remembering I and the remembered I, but he does do so in some significant passages, for instance, when discussing the character of tradition ("), _c('i', [_vm._v("FBJ")]), _vm._v(", 47–50), anarchy (ibid., 53–54), or Zionism (ibid., 54–55, 151, 166–167). The same is true of the strongly autobiographical text "), _c('i', [_vm._v("Walter Benjamin: The Story of a Friendship")]), _vm._v("; this generates extraordinary tension, precisely because Scholem does not endeavor to balance out differences and difficulties; see esp. "), _c('i', [_vm._v("Story of a Friendship")]), _vm._v(", 83–91. At times, however, he does distinguish between the remembering and remembered I; see, for example, "), _c('i', [_vm._v("Story of a Friendship")]), _vm._v(", 65ff. On the form of the autobiography, see also Mosès, “Gershom Scholems Autobiographie,” 3–15.")]), _vm._v(" "), _c('p', {
    staticClass: "note_para"
  }, [_c('a', {
    attrs: {
      "id": "rfnpt1-2"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#fnpt1-2"
    }
  }, [_vm._v("2")]), _vm._v(". Zadoff quotes from the 1946 diaries in "), _c('i', [_vm._v("From Berlin to Jerusalem and Back")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note_para"
  }, [_c('a', {
    attrs: {
      "id": "rfnpt1-3"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#fnpt1-3"
    }
  }, [_vm._v("3")]), _vm._v(". See discussion in Goetschel, “Scholem’s Diaries, Letters”; Aschheim, “The Metaphysical Psychologist: On the Life and Letters of Gershom Scholem.” On the diary in general, see esp. Lejeune, "), _c('i', [_vm._v("On Diary")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note_para"
  }, [_c('a', {
    attrs: {
      "id": "rfnpt1-4"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#fnpt1-4"
    }
  }, [_vm._v("4")]), _vm._v(". See Bollenbeck, "), _c('i', [_vm._v("Bildung und Kultur")]), _vm._v(". On the symbolic dimension of Zionism, see also Berkowitz, "), _c('i', [_vm._v("Zionist Culture and West European Jewry")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note_para"
  }, [_c('a', {
    attrs: {
      "id": "rfnpt1-5"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#fnpt1-5"
    }
  }, [_vm._v("5")]), _vm._v(". On the problematics of the individual, particularly in the German Jewish context, see Mendes-Flohr, "), _c('i', [_vm._v("Divided Passions")]), _vm._v(", chapter 1, as well as Löwy, "), _c('i', [_vm._v("Redemption and Utopia")]), _vm._v(", chapter 3.")]), _vm._v(" "), _c('p', {
    staticClass: "note_para"
  }, [_c('a', {
    attrs: {
      "id": "rfnpt1-6"
    }
  }), _c('a', {
    attrs: {
      "href": "#/text/9780253062093_Part01#fnpt1-6"
    }
  }, [_vm._v("6")]), _vm._v(". Bourdieu, "), _c('i', [_vm._v("Language and Symbolic Power")]), _vm._v(", 106. See also Bourdieu, “Intellectual Field and Creative Project,” 89–119; Ringer, “The Intellectual Field, Intellectual History,” 269–294, and the subsequent discussion. Otherwise than Bourdieu’s field theory suggests, however, we are not dealing with an economy of complete, reciprocal recognition but rather with unilaterally raised "), _c('i', [_vm._v("claims")]), _vm._v(" to legitimacy, often associated with the term “charisma.” On Scholem, see Bollack and Bourdieu, “L’identité juive,” 3–19.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-a4255352","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/text/9780253062093_Part01.vue
// module id = 84
// module chunks = 0